/**
 * RollCall.js
 * Component for managing group roll call/attendance
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Badge,
  Alert,
  Spinner,
  Button,
  Form,
  Modal,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './RollCall.css';
import moment from 'moment';
import { rollCallService, groupService, authService } from '../../services/api';
import { useDynamicBreadcrumb } from '../../helpers/useDynamicBreadcrumb';
import { findActiveSubscription, calculateSubscriptionEntries } from '../../utils/userHelpers';
import { USER_ROLES } from '../../utils/constants';

const RollCall = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [group, setGroup] = useState(null);
  const [rollCalls, setRollCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const queryDate = new URLSearchParams(location.search).get('date');
  const initialDate = queryDate && moment(queryDate, 'YYYY-MM-DD', true).isValid()
    ? moment(queryDate, 'YYYY-MM-DD').toDate()
    : new Date();

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [rollCallDates, setRollCallDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(initialDate);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [entriesStatus, setEntriesStatus] = useState({}); // Status ingressi per ogni utente
  const [discrepancies, setDiscrepancies] = useState(null); // Discrepanze tra iscritti e appello
  const [isRefreshing, setIsRefreshing] = useState(false); // Loading per refresh appello
  const [success, setSuccess] = useState(''); // Messaggio di successo
  const [showRefreshConfirmModal, setShowRefreshConfirmModal] = useState(false); // Conferma aggiornamento appello
  const [showDeleteModal, setShowDeleteModal] = useState(false); // Conferma cancellazione appello
  const [isDeleting, setIsDeleting] = useState(false); // Loading per cancellazione appello
  const isCurrentUserAdmin = authService?.hasRole ? authService.hasRole(USER_ROLES.ADMIN) : false;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateParam = params.get('date');
    if (dateParam && moment(dateParam, 'YYYY-MM-DD', true).isValid()) {
      const parsedDate = moment(dateParam, 'YYYY-MM-DD').toDate();
      setSelectedDate(parsedDate);
      setCurrentMonth(parsedDate);
    }
  }, [location.search]);


  // Breadcrumb dinamico con il nome del gruppo
  useDynamicBreadcrumb(
    location.pathname,
    group ? [
      { label: 'Gruppi', path: '/groups', icon: 'fas fa-users' },
      { label: group.name, path: `/groups/${groupId}/participants`, icon: 'fas fa-user-friends' },
      { label: 'Appello', path: null, icon: 'fas fa-clipboard-check' }
    ] : null
  );

  // Load group details
  const loadGroup = useCallback(async () => {
    try {
      const response = await groupService.getGroup(groupId);
      if (response.success) {
        setGroup(response.data);
      } else {
        setError('Errore nel caricamento del gruppo');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error loading group:', err);
    }
  }, [groupId]);

  // Load roll call dates for current month
  const loadRollCallDates = useCallback(async (date) => {
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // JS months are 0-based

      console.log('Loading roll call dates for:', { groupId, year, month });
      const response = await rollCallService.getRollCallDates(groupId, year, month);

      console.log('Roll call dates response:', response);

      if (response.success) {
        // Convert ISO strings to Date objects and normalize to midnight
        const dates = response.data.map(d => {
          const date = new Date(d);
          // Normalize to midnight local time for comparison
          return new Date(date.getFullYear(), date.getMonth(), date.getDate());
        });
        console.log('Processed roll call dates:', dates);
        setRollCallDates(dates);
      } else {
        console.error('Error loading roll call dates:', response.message);
      }
    } catch (err) {
      console.error('Error loading roll call dates:', err);
    }
  }, [groupId]);

  // Helper function to calculate entries status for a user at a specific date
  const calculateEntriesStatus = useCallback(async (userId, date) => {
    if (!group || !group.courseSubscriptions) {
      return null;
    }

    // Find active subscription for this user and date
    const userSubscription = findActiveSubscription(group.courseSubscriptions, userId, date);

    if (!userSubscription) {
      return null;
    }

    // For pay-per-use subscriptions, get attendance count
    if (userSubscription.offer.isPayPerUse || (userSubscription.offer.totalTickets != null && userSubscription.offer.totalTickets > 0)) {
      try {
        const countResponse = await rollCallService.countUserAttendanceInPeriod(
          userId,
          groupId,
          userSubscription.startDate,
          userSubscription.endDate
        );

        if (countResponse.success) {
          return calculateSubscriptionEntries(userSubscription, countResponse.data);
        }
      } catch (err) {
        console.error('Error calculating entries for user:', userId, err);
      }
    }

    // For monthly subscriptions, return immediately
    if (userSubscription.offer.isMonthly || (userSubscription.offer.durationMonths != null && userSubscription.offer.durationMonths > 0)) {
      return calculateSubscriptionEntries(userSubscription, 0);
    }

    return null;
  }, [group, groupId]);

  // Load roll calls for selected date (without auto-creating)
  const loadRollCalls = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const dateString = moment(selectedDate).format('YYYY-MM-DD');
      const response = await rollCallService.checkRollCalls(groupId, dateString);

      if (response.success) {
        setRollCalls(response.data);

        // Calculate entries status for all users with roll calls
        if (response.data && response.data.length > 0) {
          const statusPromises = response.data.map(rc =>
            calculateEntriesStatus(rc.user.id, dateString)
          );
          const statusResults = await Promise.all(statusPromises);

          const statusMap = {};
          response.data.forEach((rc, index) => {
            if (statusResults[index]) {
              statusMap[rc.user.id] = statusResults[index];
            }
          });
          setEntriesStatus(statusMap);

          // Check for discrepancies between enrolled users and roll call
          const discrepanciesResponse = await rollCallService.checkRollCallDiscrepancies(groupId, dateString);
          if (discrepanciesResponse.success) {
            const data = discrepanciesResponse.data;
            if (data.missingCount > 0 || data.expiredCount > 0) {
              setDiscrepancies(data);
              setError(`⚠️ Trovati ${data.missingCount} nuovi iscritti e ${data.expiredCount} iscrizioni non valide. Clicca 'Aggiorna Appello' per sincronizzare l'appello (rimuovere gli atleti senza iscrizione e aggiungere nuovi atleti con iscrizione valida).`);
            } else {
              setDiscrepancies(null);
            }
          }
        } else {
          // No roll calls exist for this date
          setRollCalls([]);
          setEntriesStatus({});
          setDiscrepancies(null);
        }
      } else {
        setRollCalls([]);
        setEntriesStatus({});
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error loading roll calls:', err);
      setRollCalls([]);
      setEntriesStatus({});
    } finally {
      setLoading(false);
    }
  }, [groupId, selectedDate, calculateEntriesStatus]);

  const performUpdate = async (rollCallId, isPresent) => {
    try {
      setSaving(true);

      const response = await rollCallService.updateRollCall(rollCallId, isPresent);

      if (response.success) {
        // Update local state
        setRollCalls(prev =>
          prev.map(rc =>
            rc.id === rollCallId
              ? { ...rc, isPresent }
              : rc
          )
        );
      } else {
        setError(response.message || 'Errore nell\'aggiornamento della presenza');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error updating attendance:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle attendance update with check for roll calls existence
  const handleUpdateAttendance = async (rollCallId, isPresent) => {
    // Check if roll calls exist
    if (rollCalls.length === 0) {
      // No roll calls exist, show modal to create first
      setShowCreateModal(true);
      setPendingAction({ type: 'update', rollCallId, isPresent });
      return;
    }

    // Roll calls exist, perform update
    await performUpdate(rollCallId, isPresent);
  };

  // Handle create confirmation from modal
  const handleConfirmCreate = async () => {
    try {
      setSaving(true);
      setShowCreateModal(false);

      const dateString = moment(selectedDate).format('YYYY-MM-DD');
      const response = await rollCallService.createRollCalls(groupId, dateString);

      if (response.success) {
        // Reload roll calls
        const checkResponse = await rollCallService.checkRollCalls(groupId, dateString);
        if (checkResponse.success && checkResponse.exists) {
          setRollCalls(checkResponse.data);

          // If there was a pending update action, execute it now
          if (pendingAction?.type === 'update') {
            await performUpdate(pendingAction.rollCallId, pendingAction.isPresent);
          }
        }
        // Reload roll call dates for the calendar
        await loadRollCallDates(selectedDate);
        setPendingAction(null);
      } else {
        setError(response.message || 'Errore nella creazione dell\'appello');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error creating roll calls:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowCreateModal(false);
    setPendingAction(null);
  };

  // Handle refresh roll call
  const handleRefreshRollCall = () => {
    setShowRefreshConfirmModal(true);
  };

  const performRefreshRollCall = async () => {
    setShowRefreshConfirmModal(false);
    try {
      setIsRefreshing(true);
      setError('');
      setSuccess('');

      const dateString = moment(selectedDate).format('YYYY-MM-DD');
      const response = await rollCallService.refreshRollCall(groupId, dateString);

      if (response.success && response.data.success) {
        const data = response.data;
        setSuccess(`✓ Appello aggiornato: ${data.addedCount} aggiunti, ${data.removedCount} rimossi`);
        setDiscrepancies(null);

        // Reload roll calls and entries status
        setRollCalls(data.rollCalls);

        const statusPromises = data.rollCalls.map(rc =>
          calculateEntriesStatus(rc.user.id, dateString)
        );
        const statusResults = await Promise.all(statusPromises);

        const statusMap = {};
        data.rollCalls.forEach((rc, index) => {
          if (statusResults[index]) {
            statusMap[rc.user.id] = statusResults[index];
          }
        });
        setEntriesStatus(statusMap);

        // Reload roll call dates for calendar
        await loadRollCallDates(selectedDate);
      } else {
        setError(response.data?.message || response.message || 'Errore nell\'aggiornamento dell\'appello');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error refreshing roll call:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDeleteRollCall = async () => {
    try {
      setIsDeleting(true);
      setError('');
      setSuccess('');

      const dateString = moment(selectedDate).format('YYYY-MM-DD');
      const response = await rollCallService.deleteRollCallSession(groupId, dateString);

      if (response.success && response.data?.success) {
        setShowDeleteModal(false);
        setRollCalls([]);
        setEntriesStatus({});
        setDiscrepancies(null);
        setSuccess('Appello eliminato con successo');
        await loadRollCallDates(selectedDate);
      } else {
        setShowDeleteModal(false);
        setError(response.data?.message || response.message || "Errore durante l'eliminazione dell'appello");
      }
    } catch (err) {
      setShowDeleteModal(false);
      setError('Errore di connessione al server');
      console.error('Error deleting roll call:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle date change
  const handleDateChange = (date) => {
    setSelectedDate(date);
    setDiscrepancies(null);
    setSuccess('');
  };

  // Handle month change in calendar
  const handleMonthChange = (date) => {
    setCurrentMonth(date);
    loadRollCallDates(date);
  };

  // Check if a date has roll calls
  const hasRollCall = (date) => {
    // Normalize the date to midnight for comparison
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const result = rollCallDates.some(d => {
      const normalizedD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      return normalizedD.getTime() === normalizedDate.getTime();
    });
    return result;
  };

  // Effects
  useEffect(() => {
    loadGroup();
  }, [loadGroup]);

  useEffect(() => {
    // Carica roll calls solo quando cambiano i valori effettivi
    loadRollCalls();
  }, [loadRollCalls]); // Utilizza loadRollCalls come dipendenza

  useEffect(() => {
    // Carica le date con appelli del mese corrente
    loadRollCallDates(currentMonth);
  }, [loadRollCallDates, currentMonth]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento appello...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="roll-call">
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center gap-3">
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/groups')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Torna ai Gruppi
            </Button>
            <div>
              <h2>Appello: {group?.name}</h2>
              <p className="text-muted mb-0">
                Corso: {group?.course?.name} ({group?.course?.academicYear}/{group?.course?.academicYear + 1})
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="warning" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Date Selection */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Data Appello</Form.Label>
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  onMonthChange={handleMonthChange}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  dayClassName={(date) => hasRollCall(date) ? 'has-roll-call' : undefined}
                  locale="it"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </Form.Group>
            </Col>
            <Col md={8} className="d-flex align-items-end">
              <div className="ms-3">
                {rollCalls && rollCalls.length > 0 && (
                  <div className="d-flex gap-3 align-items-center">
                    <Badge bg="success">
                      Presenti: {rollCalls.filter(rc => rc.isPresent).length}
                    </Badge>
                    <Badge bg="warning">
                      Assenti: {rollCalls.filter(rc => !rc.isPresent).length}
                    </Badge>
                    <Badge bg="info">
                      Totale: {rollCalls.length}
                    </Badge>

                    {/* Pulsante Aggiorna Appello con badge e tooltip */}
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        discrepancies && (discrepancies.missingCount > 0 || discrepancies.expiredCount > 0) ? (
                          <Tooltip id="discrepancies-tooltip">
                            <div className="text-start">
                              {discrepancies.missingCount > 0 && (
                                <div className="mb-2">
                                  <strong>Nuovi iscritti ({discrepancies.missingCount}):</strong>
                                  <ul className="mb-0 ps-3">
                                    {discrepancies.missingUsers.slice(0, 10).map(user => (
                                      <li key={user.id}>{user.firstName} {user.lastName}</li>
                                    ))}
                                    {discrepancies.missingUsers.length > 10 && (
                                      <li>...e altri {discrepancies.missingUsers.length - 10}</li>
                                    )}
                                  </ul>
                                </div>
                              )}
                              {discrepancies.expiredCount > 0 && (
                                <div>
                                  <strong>Iscrizioni scadute ({discrepancies.expiredCount}):</strong>
                                  <ul className="mb-0 ps-3">
                                    {discrepancies.expiredUsers.slice(0, 10).map(user => (
                                      <li key={user.id}>{user.firstName} {user.lastName}</li>
                                    ))}
                                    {discrepancies.expiredUsers.length > 10 && (
                                      <li>...e altri {discrepancies.expiredUsers.length - 10}</li>
                                    )}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </Tooltip>
                        ) : (
                          <Tooltip id="refresh-tooltip">
                            Sincronizza la lista partecipanti con le iscrizioni valide
                          </Tooltip>
                        )
                      }
                    >
                      <Button
                        variant={discrepancies && (discrepancies.missingCount > 0 || discrepancies.expiredCount > 0) ? "warning" : "outline-secondary"}
                        size="sm"
                        onClick={handleRefreshRollCall}
                        disabled={isRefreshing}
                        className="d-flex align-items-center gap-2"
                      >
                        {isRefreshing ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <i className="fas fa-sync-alt"></i>
                        )}
                        <span>Aggiorna</span>
                        {discrepancies && (discrepancies.missingCount > 0 || discrepancies.expiredCount > 0) && (
                          <Badge bg="danger" pill>
                            {discrepancies.missingCount + discrepancies.expiredCount}
                          </Badge>
                        )}
                      </Button>
                    </OverlayTrigger>

                    {/* Pulsante Elimina Appello (riservato all'Admin) */}
                    {isCurrentUserAdmin && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          rollCalls.some(rc => rc.isPresent) ? (
                            <Tooltip id="delete-disabled-tooltip">
                              Impossibile eliminare: ci sono atleti segnati come presenti. Impostali prima come assenti.
                            </Tooltip>
                          ) : (
                            <Tooltip id="delete-tooltip">
                              Elimina l'intero appello per questa data
                            </Tooltip>
                          )
                        }
                      >
                        <span className="d-inline-block">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => setShowDeleteModal(true)}
                            disabled={rollCalls.some(rc => rc.isPresent) || isDeleting}
                            className="d-flex align-items-center gap-2"
                            data-testid="delete-rollcall-btn"
                          >
                            {isDeleting ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <i className="fas fa-trash-alt"></i>
                            )}
                            <span>Elimina</span>
                          </Button>
                        </span>
                      </OverlayTrigger>
                    )}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Roll Call List */}
      <Card>
        <Card.Body>
          <h5 className="mb-3">Lista Partecipanti</h5>

          {!rollCalls || rollCalls.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
              <h5 className="text-muted mb-2">Nessun appello registrato</h5>
              <p className="text-muted mb-3">
                Non esiste ancora un appello per il giorno <strong>{moment(selectedDate).format('DD/MM/YYYY')}</strong>.
              </p>
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
                data-testid="create-rollcall-btn"
              >
                <i className="fas fa-plus me-2"></i>
                Crea Appello
              </Button>
            </div>
          ) : (
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th className="text-center">Presenza</th>
                  <th className="text-center">Ingressi</th>
                  <th className="text-center">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {rollCalls.map((rollCall) => {
                  const userStatus = entriesStatus[rollCall.user.id];
                  const hasPayPerUse = userStatus && !userStatus.hasActiveMonthlySubscription && userStatus.totalEntries > 0;

                  return (
                    <tr key={rollCall.id}>
                      <td>
                        <Link
                          to={`/users/${rollCall.user.id}/edit`}
                          className="text-decoration-none"
                        >
                          <strong>
                            {rollCall.user.firstName} {rollCall.user.lastName}
                          </strong>
                        </Link>
                      </td>
                      <td className="text-center">
                        <Badge bg={rollCall.isPresent ? 'success' : 'danger'}>
                          {rollCall.isPresent ? 'Presente' : 'Assente'}
                        </Badge>
                      </td>
                      <td className="text-center">
                        {hasPayPerUse ? (
                          <Badge
                            bg={
                              userStatus.remainingEntries === 0 ? 'danger' :
                                userStatus.isRunningOut ? 'warning' :
                                  'info'
                            }
                            title={`${userStatus.usedEntries}/${userStatus.totalEntries} ingressi utilizzati`}
                          >
                            {userStatus.remainingEntries} rimanenti
                          </Badge>
                        ) : userStatus?.hasActiveMonthlySubscription ? (
                          <Badge bg="success" title="Abbonamento mensile attivo">
                            <i className="fas fa-infinity"></i> Illimitato
                          </Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            variant={rollCall.isPresent ? 'outline-success' : 'success'}
                            size="sm"
                            onClick={() => handleUpdateAttendance(rollCall.id, true)}
                            disabled={saving || rollCall.isPresent}
                            title="Segna presente"
                          >
                            <i className="fas fa-check"></i>
                          </Button>
                          <Button
                            variant={!rollCall.isPresent ? 'outline-danger' : 'danger'}
                            size="sm"
                            onClick={() => handleUpdateAttendance(rollCall.id, false)}
                            disabled={saving || !rollCall.isPresent}
                            title="Segna assente"
                          >
                            <i className="fas fa-times"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {saving && (
        <div className="position-fixed top-50 start-50 translate-middle">
          <div className="bg-white p-3 rounded shadow">
            <Spinner animation="border" size="sm" className="me-2" />
            Aggiornamento in corso...
          </div>
        </div>
      )}

      {/* Refresh Roll Call Confirmation Modal */}
      <Modal show={showRefreshConfirmModal} onHide={() => setShowRefreshConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-sync-alt me-2"></i>
            Aggiorna Appello
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Vuoi sincronizzare l'appello del <strong>{moment(selectedDate).format('DD/MM/YYYY')}</strong> con le iscrizioni valide?
          <br />
          <br />
          Gli atleti senza iscrizione attiva o archiviati saranno rimossi e i nuovi iscritti verranno aggiunti.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowRefreshConfirmModal(false)}
          >
            Annulla
          </Button>
          <Button
            variant="primary"
            onClick={performRefreshRollCall}
          >
            Aggiorna
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Roll Call Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => !isDeleting && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title className="text-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Elimina Appello
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Vuoi davvero eliminare l'appello del <strong>{moment(selectedDate).format('DD/MM/YYYY')}</strong> per il gruppo <strong>{group?.name}</strong>?
          <br />
          <br />
          Tutti i <strong>{rollCalls.length}</strong> record dei partecipanti per questa data verranno rimossi permanentemente. L'operazione non può essere annullata.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
          >
            Annulla
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteRollCall}
            disabled={isDeleting}
            data-testid="confirm-delete-btn"
          >
            {isDeleting && <Spinner animation="border" size="sm" className="me-2" />}
            Elimina Appello
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Roll Call Confirmation Modal */}
      <Modal show={showCreateModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-clipboard-check me-2"></i>
            Crea Appello
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Non esiste ancora un appello per il giorno <strong>{moment(selectedDate).format('DD/MM/YYYY')}</strong>.
          <br />
          <br />
          Vuoi creare l'appello per tutti i partecipanti attivi del gruppo?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            disabled={saving}
          >
            Annulla
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmCreate}
            disabled={saving}
          >
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Crea Appello
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RollCall;